// Constellations — the structured data (customer / project / dept facets)
// rendered as hubs that gravitationally collect their atoms, while every atom
// is also pulled toward its UMAP position. Structure and semantics literally
// fight over each node; bridges between hubs are atoms that belong to both.
// Hub shape encodes kind (circle=customer, square=project, diamond=dept);
// hub ring colour is knowledge weather (status colours, reserved for status).
import { useEffect, useMemo, useRef } from "react";
import {
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
  type Simulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from "d3-force";
import type { AtlasAtom } from "../../../lib/atlas-types";
import {
  type Camera,
  type Tokens,
  type TooltipPayload,
  newCamera,
  fitCameraToBounds,
  quantileBounds,
  worldToScreen,
  screenToWorld,
  viewScale,
  zoomAt,
  rgba,
  drawHaloText,
  ageLabel,
  fitCanvas,
  prefersReducedMotion,
  MONO_10,
} from "./atlas-canvas";

export type HubKind = "customer" | "project" | "dept";

interface Hub {
  key: string; // graph nodeKey, e.g. customer:flightcare
  kind: HubKind;
  id: string;
  count: number;
  members: number[]; // atom indices
  r: number;
  cx: number; // seed centroid (sim units)
  cy: number;
}

interface SimNode extends SimulationNodeDatum {
  id: string;
  hub?: Hub;
  atomIdx?: number;
  seedX: number;
  seedY: number;
  r: number;
}

interface Props {
  atoms: AtlasAtom[];
  visible: boolean[];
  weather: Record<string, string> | null; // nodeKey → green|amber|red|grey
  colorOf: (a: AtlasAtom) => string;
  tokens: Tokens;
  selectedKey: string | null;
  onSelect: (a: AtlasAtom | null) => void;
  onHover: (h: TooltipPayload | null) => void;
}

const TAU = Math.PI * 2;
const SIM = 1000; // sim-space = world × 1000 (d3-force defaults assume px-ish scales)
const MAX_HUBS = 64; // ponytail: label soup above this; top-by-count wins

export function ConstellationView(props: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const camRef = useRef<Camera>(newCamera());
  const simRef = useRef<{ sim: Simulation<SimNode, SimulationLinkDatum<SimNode>>; nodes: SimNode[]; links: SimulationLinkDatum<SimNode>[] } | null>(null);
  const hoverRef = useRef<SimNode | null>(null);
  const dragRef = useRef<{ mode: "none" | "pan" | "hub"; node?: SimNode; moved: boolean; x: number; y: number }>({ mode: "none", moved: false, x: 0, y: 0 });
  const loopRef = useRef(0);
  const rafRef = useRef(0);
  const propsRef = useRef(props);
  propsRef.current = props;
  const drawRef = useRef<() => void>(() => {});

  // ---- hub graph from the filtered atoms ----
  const { atoms, visible } = props;
  const build = useMemo(() => {
    const map = new Map<string, Hub>();
    const add = (key: string, kind: HubKind, id: string, idx: number) => {
      let hub = map.get(key);
      if (!hub) {
        hub = { key, kind, id, count: 0, members: [], r: 0, cx: 0, cy: 0 };
        map.set(key, hub);
      }
      hub.count++;
      hub.members.push(idx);
    };
    let shown = 0;
    atoms.forEach((a, i) => {
      if (!visible[i]) return;
      shown++;
      for (const c of a.customer) add(`customer:${c}`, "customer", c, i);
      for (const p of a.project) add(`project:${p}`, "project", p, i);
      if (a.scope.startsWith("dept:")) add(`dept:${a.scope.slice(5)}`, "dept", a.scope.slice(5), i);
    });
    const hubs = [...map.values()]
      // <3 members = confetti; >30% of the corpus = a non-discriminative facet
      // (customer:aeronet tags half the brain) that swallows the whole layout.
      .filter((hub) => (hub.kind === "dept" || hub.count >= 3) && hub.count <= Math.max(60, shown * 0.3))
      .sort((a, b) => b.count - a.count)
      .slice(0, MAX_HUBS);
    for (const hub of hubs) {
      hub.r = 7 + Math.sqrt(hub.count) * 2.1;
      hub.cx = (hub.members.reduce((s, i) => s + atoms[i].x, 0) / hub.count) * SIM;
      hub.cy = (hub.members.reduce((s, i) => s + atoms[i].y, 0) / hub.count) * SIM;
    }
    return hubs;
  }, [atoms, visible]);

  const fittedRef = useRef(false);

  drawRef.current = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const state = simRef.current;
    if (!canvas || !ctx || !state) return;
    const [w, h] = fitCanvas(canvas);
    // Fit-to-data on the first real draw — mount-time rects can race flex layout.
    if (!fittedRef.current && w > 40 && h > 40) {
      const b = quantileBounds(propsRef.current.atoms, propsRef.current.visible);
      if (b) {
        camRef.current = fitCameraToBounds(b[0], b[1], b[2], b[3], w, h);
        fittedRef.current = true;
      }
    }
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const { colorOf, tokens: T, weather, selectedKey } = propsRef.current;
    const cam = camRef.current;
    const kPow = Math.pow(cam.k, 0.35);

    ctx.fillStyle = T.surface;
    ctx.fillRect(0, 0, w, h);

    // Same instrument-bed grid as the semantic map.
    const g = 1 / 20;
    if (viewScale(cam, w, h) * g >= 13) {
      const [wx0, wy0] = screenToWorld(0, 0, cam, w, h);
      const [wx1, wy1] = screenToWorld(w, h, cam, w, h);
      const i0 = Math.floor(wx0 / g), i1 = Math.ceil(wx1 / g);
      const j0 = Math.floor(wy0 / g), j1 = Math.ceil(wy1 / g);
      if ((i1 - i0) * (j1 - j0) < 6000) {
        ctx.fillStyle = rgba(T.borderStrong, 0.4);
        for (let i = i0; i <= i1; i++) {
          for (let j = j0; j <= j1; j++) {
            const [px, py] = worldToScreen(i * g, j * g, cam, w, h);
            ctx.fillRect(px - 0.5, py - 0.5, 1.2, 1.2);
          }
        }
      }
    }

    const pos = (n: SimNode): [number, number] => worldToScreen((n.x ?? 0) / SIM, (n.y ?? 0) / SIM, cam, w, h);

    // Hover isolation set.
    const hovered = hoverRef.current;
    let memberSet: Set<number> | null = null;
    if (hovered?.hub) memberSet = new Set(hovered.hub.members);

    // Links.
    ctx.lineWidth = 1;
    for (const l of state.links) {
      const s = l.source as SimNode;
      const t = l.target as SimNode;
      const inFocus = !memberSet || s === hovered || (t.atomIdx !== undefined && memberSet.has(t.atomIdx));
      ctx.strokeStyle = rgba(T.borderStrong, inFocus ? 0.34 : 0.08);
      const [x1, y1] = pos(s);
      const [x2, y2] = pos(t);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }

    // Atom nodes.
    for (const n of state.nodes) {
      if (n.atomIdx === undefined) continue;
      const a = propsRef.current.atoms[n.atomIdx];
      const [x, y] = pos(n);
      if (x < -20 || x > w + 20 || y < -20 || y > h + 20) continue;
      const dim = memberSet && !memberSet.has(n.atomIdx);
      const color = colorOf(a);
      const r = n.r * kPow;
      ctx.globalAlpha = dim ? 0.15 : 1;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, TAU);
      if (!a.status || a.status === "active" || a.status === "draft") {
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = T.surface;
        ctx.lineWidth = 1.1;
        ctx.stroke();
      } else {
        ctx.fillStyle = rgba(color, 0.13);
        ctx.fill();
        ctx.strokeStyle = rgba(color, 0.55);
        ctx.lineWidth = 1.1;
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      if (selectedKey && a.key === selectedKey) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(x, y, r + 3.5, 0, TAU);
        ctx.stroke();
      }
    }

    // Hubs on top.
    const weatherColor = (key: string): string => {
      const state = propsRef.current.weather?.[key];
      if (state === "green") return T.statusGreen;
      if (state === "amber") return T.statusAmber;
      if (state === "red") return T.statusRed;
      return T.textMuted;
    };
    for (const n of state.nodes) {
      if (!n.hub) continue;
      const [x, y] = pos(n);
      if (x < -60 || x > w + 60 || y < -60 || y > h + 60) continue;
      const hub = n.hub;
      const r = hub.r * kPow;
      const ring = weatherColor(hub.key);
      const isHover = hovered === n;
      ctx.fillStyle = T.surface;
      ctx.strokeStyle = ring;
      ctx.lineWidth = isHover ? 3 : 2;
      ctx.beginPath();
      if (hub.kind === "customer") {
        ctx.arc(x, y, r, 0, TAU);
      } else if (hub.kind === "project") {
        const s = r * 1.75;
        roundRectPath(ctx, x - s / 2, y - s / 2, s, s, Math.min(6, r * 0.45));
      } else {
        ctx.moveTo(x, y - r * 1.2);
        ctx.lineTo(x + r * 1.2, y);
        ctx.lineTo(x, y + r * 1.2);
        ctx.lineTo(x - r * 1.2, y);
        ctx.closePath();
      }
      ctx.fill();
      ctx.stroke();
      ctx.font = MONO_10;
      ctx.fillStyle = T.textTertiary;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(hub.count), x, y + 0.5);
      if (hub.count >= 3 || cam.k > 1.5 || isHover) {
        drawHaloText(ctx, hub.id, x, y + r + (hub.kind === "customer" ? 11 : 14), T.inkStrong, rgba(T.surface, 0.9));
      }
    }

    if (weather === null) {
      // weather is best-effort; nothing to draw differently — rings fall back to grey.
    }
  };

  const scheduleDraw = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      drawRef.current();
    });
  };

  const startLoop = () => {
    if (loopRef.current) return;
    const step = () => {
      loopRef.current = 0;
      const state = simRef.current;
      if (!state) return;
      state.sim.tick();
      drawRef.current();
      if (state.sim.alpha() > 0.004) loopRef.current = requestAnimationFrame(step);
    };
    loopRef.current = requestAnimationFrame(step);
  };

  // ---- (re)build the simulation when the hub graph changes ----
  useEffect(() => {
    const { atoms: as } = propsRef.current;
    const vis = propsRef.current.visible;
    const nodes: SimNode[] = [];
    const byId = new Map<string, SimNode>();
    for (const hub of build) {
      const n: SimNode = { id: hub.key, hub, seedX: hub.cx, seedY: hub.cy, r: hub.r, x: hub.cx, y: hub.cy };
      nodes.push(n);
      byId.set(n.id, n);
    }
    as.forEach((a, i) => {
      if (!vis[i]) return;
      const n: SimNode = { id: a.key, atomIdx: i, seedX: a.x * SIM, seedY: a.y * SIM, r: 3.1, x: a.x * SIM, y: a.y * SIM };
      nodes.push(n);
      byId.set(n.id, n);
    });
    const links: SimulationLinkDatum<SimNode>[] = [];
    for (const hub of build) {
      for (const m of hub.members) {
        const t = byId.get(as[m].key);
        if (t) links.push({ source: hub.key, target: t.id });
      }
    }
    const sim = forceSimulation<SimNode>(nodes)
      .force(
        "link",
        forceLink<SimNode, SimulationLinkDatum<SimNode>>(links)
          .id((n) => n.id)
          .distance((l) => (l.source as SimNode).r + 24)
          .strength(0.32)
      )
      .force("charge", forceManyBody<SimNode>().strength((n) => (n.hub ? -170 : -9)).distanceMax(340))
      .force("collide", forceCollide<SimNode>().radius((n) => n.r + (n.hub ? 13 : 1.3)))
      .force("sx", forceX<SimNode>((n) => n.seedX).strength((n) => (n.hub ? 0.03 : 0.055)))
      .force("sy", forceY<SimNode>((n) => n.seedY).strength((n) => (n.hub ? 0.03 : 0.055)))
      .velocityDecay(0.32)
      .alphaDecay(0.028)
      .stop();
    simRef.current = { sim, nodes, links };
    if (prefersReducedMotion()) {
      for (let i = 0; i < 280 && sim.alpha() > 0.004; i++) sim.tick();
      scheduleDraw();
    } else {
      startLoop();
    }
    return () => {
      sim.stop();
      if (loopRef.current) {
        cancelAnimationFrame(loopRef.current);
        loopRef.current = 0;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [build]);

  // Re-render on presentation-prop changes (colour mode, selection, weather).
  useEffect(scheduleDraw, [props.colorOf, props.selectedKey, props.weather, props.tokens]);

  // ---- pointer interaction ----
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const nodeAt = (mx: number, my: number): SimNode | null => {
      const state = simRef.current;
      if (!state) return null;
      const rect = canvas.getBoundingClientRect();
      const cam = camRef.current;
      const kPow = Math.pow(cam.k, 0.35);
      let bestAtom: SimNode | null = null;
      let bestD = Infinity;
      // hubs take priority (bigger targets, drawn on top)
      for (const n of state.nodes) {
        const [x, y] = worldToScreen((n.x ?? 0) / SIM, (n.y ?? 0) / SIM, cam, rect.width, rect.height);
        const d = Math.hypot(x - mx, y - my);
        if (n.hub) {
          if (d < n.hub.r * kPow + 6) return n;
        } else if (d < Math.max(8, n.r * kPow + 3) && d < bestD) {
          bestD = d;
          bestAtom = n;
        }
      }
      return bestAtom;
    };

    const reportHover = (n: SimNode | null, mx: number, my: number) => {
      const p = propsRef.current;
      if (!n) {
        p.onHover(null);
        return;
      }
      if (n.hub) {
        const wx = p.weather?.[n.hub.key] ?? "grey";
        p.onHover({ x: mx, y: my, title: n.hub.id, meta: `${n.hub.kind} · ${n.hub.count} atoms · weather ${wx}` });
      } else if (n.atomIdx !== undefined) {
        const a = p.atoms[n.atomIdx];
        p.onHover({ x: mx, y: my, title: a.title, meta: `${a.type} · ${a.scope} · ${ageLabel(a.created_at)}`, swatch: p.colorOf(a) });
      }
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      camRef.current = zoomAt(camRef.current, e.clientX - rect.left, e.clientY - rect.top, Math.exp(-e.deltaY * 0.0016), rect.width, rect.height);
      scheduleDraw();
    };
    const onDown = (e: PointerEvent) => {
      // Left/primary only — a right-click's pointerup is swallowed by the
      // context menu, which would leave a stuck pan or a pinned hub.
      if (e.button !== 0 || !e.isPrimary) return;
      canvas.setPointerCapture(e.pointerId);
      const hit = nodeAt(e.offsetX, e.offsetY);
      if (hit?.hub) {
        dragRef.current = { mode: "hub", node: hit, moved: false, x: e.offsetX, y: e.offsetY };
      } else {
        dragRef.current = { mode: "pan", moved: false, x: e.offsetX, y: e.offsetY };
      }
    };
    const onCancel = () => {
      const d = dragRef.current;
      if (d.mode === "hub" && d.node) {
        d.node.fx = null;
        d.node.fy = null;
      }
      dragRef.current = { mode: "none", moved: false, x: 0, y: 0 };
    };
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (d.mode === "hub" && d.node) {
        const rect = canvas.getBoundingClientRect();
        const [wx, wy] = screenToWorld(e.offsetX, e.offsetY, camRef.current, rect.width, rect.height);
        d.node.fx = wx * SIM;
        d.node.fy = wy * SIM;
        d.moved = true;
        const state = simRef.current;
        if (state) {
          state.sim.alpha(Math.max(state.sim.alpha(), 0.3));
          startLoop();
        }
        return;
      }
      if (d.mode === "pan") {
        const dx = e.offsetX - d.x;
        const dy = e.offsetY - d.y;
        if (Math.abs(dx) + Math.abs(dy) > 2) d.moved = true;
        camRef.current.panX += dx;
        camRef.current.panY += dy;
        d.x = e.offsetX;
        d.y = e.offsetY;
        propsRef.current.onHover(null);
        scheduleDraw();
        return;
      }
      const hit = nodeAt(e.offsetX, e.offsetY);
      if (hit !== hoverRef.current) {
        hoverRef.current = hit;
        scheduleDraw();
      }
      reportHover(hit, e.offsetX, e.offsetY);
    };
    const onUp = (e: PointerEvent) => {
      const d = dragRef.current;
      if (d.mode === "hub" && d.node) {
        d.node.fx = null;
        d.node.fy = null;
      }
      if (!d.moved && d.mode !== "none") {
        const hit = nodeAt(e.offsetX, e.offsetY);
        if (hit?.atomIdx !== undefined) propsRef.current.onSelect(propsRef.current.atoms[hit.atomIdx]);
        else if (!hit) propsRef.current.onSelect(null);
      }
      dragRef.current = { mode: "none", moved: false, x: 0, y: 0 };
    };
    const onLeave = () => {
      dragRef.current.mode = "none";
      if (hoverRef.current) {
        hoverRef.current = null;
        propsRef.current.onHover(null);
        scheduleDraw();
      }
    };

    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointerleave", onLeave);
    canvas.addEventListener("pointercancel", onCancel);
    canvas.addEventListener("lostpointercapture", onCancel);
    const ro = new ResizeObserver(scheduleDraw);
    ro.observe(canvas);
    return () => {
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointerleave", onLeave);
      canvas.removeEventListener("pointercancel", onCancel);
      canvas.removeEventListener("lostpointercapture", onCancel);
      ro.disconnect();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0; // reset the guard or StrictMode remount deadlocks scheduleDraw()
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <canvas ref={canvasRef} style={{ cursor: "grab" }} aria-label="Constellation graph of entities and atoms" role="img" />;
}

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
